import { CheckCircleIcon, ExclamationIcon } from "@heroicons/react/outline";

import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";
import { ERROR_TYPE_LABELS, RequestErrorType, RequestLogStatus } from "@/types/request-status";

interface RequestStatusBadgeProps {
  status: RequestLogStatus;
  size?: BadgeSize;
  className?: string;
  errorType?: RequestErrorType | null;
  errorMessage?: string | null;
  onRemove?: () => void;
}

const statusToVariant: Record<RequestLogStatus, BadgeVariant> = {
  SUCCESS: BadgeVariant.GREEN,
  WARNING: BadgeVariant.AMBER,
  ERROR: BadgeVariant.RED,
};

const statusDisplayLabels: Record<RequestLogStatus, string> = {
  SUCCESS: "Success",
  WARNING: "Warning",
  ERROR: "Error",
};

const StatusIcon = ({ status, size }: { status: RequestLogStatus; size: BadgeSize }) => {
  let iconClass = "h-4 w-4";
  if (size === BadgeSize.XS) {
    iconClass = "h-2.5 w-2.5";
  } else if (size === BadgeSize.SM) {
    iconClass = "h-3 w-3";
  }

  switch (status) {
    case "SUCCESS":
      return <CheckCircleIcon className={iconClass} />;
    case "WARNING":
      return <ExclamationIcon className={iconClass} />;
    case "ERROR":
      return <ExclamationIcon className={iconClass} />;
    default:
      return null;
  }
};

export const getRequestStatusDisplayLabel = (status: RequestLogStatus) =>
  statusDisplayLabels[status];

export const RequestStatusBadge = ({
  status,
  size = BadgeSize.MD,
  className,
  errorType,
  errorMessage,
  onRemove,
}: RequestStatusBadgeProps) => {
  const variant = statusToVariant[status];
  const icon = <StatusIcon status={status} size={size} />;

  const badge = (
    <Badge variant={variant} size={size} className={className} onRemove={onRemove}>
      {icon}
      {statusDisplayLabels[status]}
    </Badge>
  );

  // Show tooltip with error details for WARNING and ERROR statuses
  if ((status === "WARNING" || status === "ERROR") && (errorType || errorMessage)) {
    // Only show error label if errorType is a valid key in ERROR_TYPE_LABELS
    const errorLabel =
      errorType && errorType in ERROR_TYPE_LABELS ? ERROR_TYPE_LABELS[errorType] : null;

    const tooltipContent = (
      <div className="max-w-xs">
        {errorLabel && <div className="font-medium">{errorLabel}</div>}
        {errorMessage && (
          <div className={cn("text-xs", errorLabel ? "mt-1 text-gray-300" : "font-medium")}>
            {errorMessage}
          </div>
        )}
      </div>
    );

    return (
      <BasicTooltip content={tooltipContent} side="top">
        {badge}
      </BasicTooltip>
    );
  }

  return badge;
};

export default RequestStatusBadge;
